import { tr } from "./localization";
import type { Task } from "./types";
export const priorities: Record<string, string> = {
  low: tr("Низкий"),
  normal: tr("Обычный"),
  high: tr("Высокий"),
  urgent: tr("Срочно"),
};
export const kinds: Record<string, string> = {
  backlog: tr("Бэклог"),
  todo: tr("К выполнению"),
  in_progress: tr("В работе"),
  review: tr("На проверке"),
  done: tr("Готово"),
};
export const dayInAlmaty = (d = new Date()) =>
  new Intl.DateTimeFormat("en-CA", {
    timeZone: "Asia/Almaty",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(d);
const MONTH_DAY = (d: Date) =>
  d
    .toLocaleDateString("ru-RU", { day: "numeric", month: "short" })
    .replace(".", "");
/** «Сегодня», «Завтра», «Пт, 10 окт» (ближайшая неделя) или «28 окт»; без точки после месяца. */
export function dateLabel(date: string) {
  const today = dayInAlmaty();
  if (date === today) return tr("Сегодня");
  if (date === dayInAlmaty(new Date(Date.now() + 86400000)))
    return tr("Завтра");
  const d = new Date(date + "T12:00:00");
  const days = Math.round(
    (d.getTime() - new Date(today + "T12:00:00").getTime()) / 86400000,
  );
  if (days > 1 && days < 7) {
    const weekday = d.toLocaleDateString("ru-RU", { weekday: "short" });
    return (
      weekday.charAt(0).toUpperCase() +
      weekday.slice(1).replace(".", "") +
      ", " +
      MONTH_DAY(d)
    );
  }
  return MONTH_DAY(d);
}
export function dueLabel(t: Task) {
  if (t.completed_at) return tr("Завершено");
  if (t.submitted_at) return tr("На приёмке у автора");
  if (!t.due_date) return tr("Без срока");
  const past = t.due_at && new Date(t.due_at) < new Date();
  if (past) return overdueText(t);
  return (
    dateLabel(t.due_date) + (t.due_time ? ", " + t.due_time.slice(0, 5) : "")
  );
}
export function group(t: Task, delegated: boolean) {
  if (t.completed_at) return tr("Недавно завершены");
  if (t.submitted_at)
    return delegated ? tr("Ждут вашей приёмки") : tr("На приёмке у автора");
  if (delegated && !t.assignee_id) return tr("Без исполнителя");
  if (t.due_at && new Date(t.due_at) < new Date()) return tr("Просрочено");
  if (delegated) return tr("В работе");
  if (!t.due_date) return tr("Без срока");
  if (t.due_date === dayInAlmaty()) return tr("Сегодня");
  const today = new Date(dayInAlmaty() + "T12:00:00");
  const end = new Date(today);
  end.setDate(today.getDate() + (7 - (today.getDay() || 7)));
  return t.due_date <= dayInAlmaty(end) ? tr("На этой неделе") : tr("Позже");
}
export const initials = (name: string) =>
  name
    .split(" ")
    .slice(0, 2)
    .map((n) => n[0])
    .join("");

/** Дата срока для карточки: «Сегодня», «Завтра» или «5 окт.» + время; без слов «просрочено». */
export function dueText(t: Task) {
  if (!t.due_date) return tr("Без срока");
  return (
    dateLabel(t.due_date) + (t.due_time ? ", " + t.due_time.slice(0, 5) : "")
  );
}
/** «сегодня в 17:40», «вчера в 17:40» или «5 окт.» — когда что-то произошло. */
export function sinceText(iso: string) {
  const d = new Date(iso),
    day = dayInAlmaty(d);
  const time = d.toLocaleTimeString("ru-RU", {
    hour: "2-digit",
    minute: "2-digit",
    timeZone: "Asia/Almaty",
  });
  if (day === dayInAlmaty()) return tr("сегодня в ") + time;
  if (day === dayInAlmaty(new Date(Date.now() - 86400000)))
    return tr("вчера в ") + time;
  return d
    .toLocaleDateString("ru-RU", {
      day: "numeric",
      month: "short",
      timeZone: "Asia/Almaty",
    })
    .replace(".", "");
}
export const plural = (n: number, one: string, few: string, many: string) =>
  n % 10 === 1 && n % 100 !== 11
    ? one
    : n % 10 >= 2 && n % 10 <= 4 && (n % 100 < 10 || n % 100 >= 20)
      ? few
      : many;
/** «Просрочено на 2 дня» — по полному числу суток. */
export function overdueText(t: Task) {
  const days = t.due_at
    ? Math.floor((Date.now() - new Date(t.due_at).getTime()) / 86400000)
    : 0;
  return days >= 1
    ? tr("Просрочено на ") + days + " " + plural(days, "день", "дня", "дней")
    : tr("Просрочено");
}

/**
 * «Руслан Ким» → «Руслана Кима». Простые правила для типичных русских и казахских имён;
 * слова не из кириллицы и неочевидные случаи остаются без изменений.
 */
export function genitive(full: string) {
  const parts = full.trim().split(/\s+/);
  if (parts.length < 1 || !parts.every((w) => /^[А-ЯЁа-яё-]+$/.test(w)))
    return full;
  const female =
    /(ова|ева|ёва|ина|ына|ая)$/.test(parts[parts.length - 1]) ||
    (parts.length > 1 &&
      /[ая]$/.test(parts[0]) &&
      !/^(Никита|Илья|Лука|Фома)$/.test(parts[0]));
  const word = (w: string, last: boolean) => {
    if (female) {
      if (/(ова|ева|ёва|ина|ына)$/.test(w)) return w.slice(0, -1) + "ой";
      if (/ая$/.test(w)) return w.slice(0, -2) + "ой";
      if (/ия$/.test(w)) return w.slice(0, -1) + "и";
      if (/[гкхжчшщ]а$/.test(w)) return w.slice(0, -1) + "и";
      if (/а$/.test(w)) return w.slice(0, -1) + "ы";
      if (/я$/.test(w)) return w.slice(0, -1) + "и";
      return w;
    }
    if (/(ский|цкий|ной|ый|ий)$/.test(w) && last)
      return w.replace(
        /(ский|цкий|ной|ый|ий|ой)$/,
        (m) => m.slice(0, -2) + "ого",
      );
    if (/[йь]$/.test(w)) return w.slice(0, -1) + "я";
    if (/[бвгджзклмнпрстфхцчшщ]$/i.test(w)) return w + "а";
    return w;
  };
  return parts.map((w, i) => word(w, i === parts.length - 1)).join(" ");
}

/** «сегодня», «вчера» или «3 окт» — для подписей «Сдано вчера», «Принято 3 окт». */
export function dayWord(iso: string) {
  const d = new Date(iso),
    day = dayInAlmaty(d);
  if (day === dayInAlmaty()) return tr("сегодня");
  if (day === dayInAlmaty(new Date(Date.now() - 86400000))) return tr("вчера");
  return d
    .toLocaleDateString("ru-RU", {
      day: "numeric",
      month: "short",
      timeZone: "Asia/Almaty",
    })
    .replace(".", "");
}

/** «вчера, 10:12» / «сегодня, 15:39» / «3 окт, 10:12» — время события в ленте. */
export function whenShort(iso: string) {
  const time = new Date(iso).toLocaleTimeString("ru-RU", {
    hour: "2-digit",
    minute: "2-digit",
    timeZone: "Asia/Almaty",
  });
  return dayWord(iso) + ", " + time;
}

/** Короткое время уведомления в часовом поясе Алматы. */
export function notificationTime(iso: string, now = new Date()) {
  const date = new Date(iso);
  const minutes = Math.max(
    0,
    Math.floor((now.getTime() - date.getTime()) / 60000),
  );
  const day = dayInAlmaty(date);
  if (day === dayInAlmaty(now) && minutes < 60)
    return minutes < 1 ? tr("Только что") : `${minutes} ${tr("мин назад")}`;
  if (day === dayInAlmaty(now) && minutes < 180) {
    const hours = Math.floor(minutes / 60);
    return `${hours} ${tr(plural(hours, "час назад", "часа назад", "часов назад"))}`;
  }
  const time = date.toLocaleTimeString("ru-RU", {
    hour: "2-digit",
    minute: "2-digit",
    timeZone: "Asia/Almaty",
  });
  if (day === dayInAlmaty(now)) return `${tr("Сегодня")}, ${time}`;
  if (day === dayInAlmaty(new Date(now.getTime() - 86400000)))
    return `${tr("Вчера")}, ${time}`;
  return `${date.toLocaleDateString("ru-RU", {
    day: "numeric",
    month: "short",
    year: date.getFullYear() !== now.getFullYear() ? "numeric" : undefined,
    timeZone: "Asia/Almaty",
  })}, ${time}`;
}
